import * as React from 'react';
import { DraggableList, DraggableListProps } from '@common/components/draggable-list';
import { Page, PageItem } from './component/page';
import { MessageConfirmParams } from '@common/hooks/message';
import { DraggableListDataItem } from '@common/components/draggable-list/draggable-list.types';

import './pages.styles.scss';

export type PagesProps<Item extends PageItem> = {
  data: Array<Item>;
  getClassName?: (page: Item) => string;
  canChangePageOrder: (page: Item) => boolean;
  canShowHidePage: (page: Item) => boolean;
  canDeletePage: (page: Item) => boolean;
  canEditPage?: (page: Item) => boolean;
  showDeleteConfirmMessage: (props: MessageConfirmParams) => void;
  onOrderChange: (data: Array<Item>) => void;
  onEdit: (page: Item) => void;
  onDelete: (page: Item) => void;
  onShowStatusChange: (page: Item, status: boolean) => void;
};

export function Pages<T extends PageItem>(props: PagesProps<T>) {
  const { 
    data, 
    onOrderChange, 
    onEdit, 
    onDelete, 
    onShowStatusChange, 
    showDeleteConfirmMessage,
    canShowHidePage, 
    canEditPage, 
    canDeletePage,
    canChangePageOrder,
    getClassName,
  } = props;

  const draggablePages = React.useMemo(() => {
    return data.filter(canChangePageOrder);
  }, [data, canChangePageOrder]);

  const nonDraggablePages = React.useMemo(() => {
    return data.filter((x) => !canChangePageOrder(x));
  }, [data, canChangePageOrder]);

  const handleOrderChange = React.useCallback((newPagesOrder: Array<T>) => {
    onOrderChange([
      ...newPagesOrder,
      ...nonDraggablePages,
    ]);
  }, [onOrderChange, nonDraggablePages]);

  const renderItem = React.useCallback((page: T) => {
    return (
      <Page<T>
        data={page}
        features={{
          delete: canDeletePage(page),
          edit: canEditPage ? canEditPage(page) : true,
          changeShowStatus: canShowHidePage(page)
        }}
        showDeleteConfirmMessage={showDeleteConfirmMessage}
        onDelete={onDelete}
        onEdit={onEdit}
        onShowStatusChange={onShowStatusChange}
      />
    );
  }, [
    onDelete, 
    onEdit, 
    onShowStatusChange, 
    showDeleteConfirmMessage, 
    canShowHidePage, 
    canEditPage, 
    canDeletePage
  ]);

  const handleGetItemClassName = React.useCallback((page: DraggableListDataItem) => {
    const customClass = getClassName ? getClassName(page as T) : '';

    return `FhqDesignToolQuestionnaireDesignerPages__item ${customClass}`;
  }, [getClassName]);

  return (
    <>
      {Boolean(draggablePages.length) && (
        <DraggableList
          classes={{
            item: handleGetItemClassName,
          }}
          items={draggablePages}
          className="FhqDesignToolQuestionnaireDesignerPages"
          renderItem={renderItem as DraggableListProps['renderItem']}
          onChange={handleOrderChange as DraggableListProps['onChange']}
        />
      )}
      <div className="FhqDesignToolQuestionnaireDesignerPages">
        {nonDraggablePages.map((x) => (
          <div key={x.id} className={handleGetItemClassName(x)}>
            {renderItem(x)}
          </div>
        ))}
      </div>
    </>
  );
};